import {
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { MedicalProvider } from '@/services/types/treatment-types';

import { StatusOptions } from '../../constants';
import { ChannelSubmitStep } from './ChannelSubmitStep';
import { InvoiceReadyStep } from './InvoiceReadyStep';
import { RecordIngestedStep } from './RecordIngestedStep';
import {
  BaseRequestStep,
  BaseRequestStepProps,
} from './RequestStatusTrackerStep';

// Steps configuration for the records pizza tracker
export const REQUEST_STEPS: Array<{
  currentStatus: TreatmentRecordRequestStatus;
  nextStatus: TreatmentRecordRequestStatus;
  title: string;
  actionText: string;
  component: React.ComponentType<BaseRequestStepProps>;
}> = [
  {
    currentStatus: 'not_ready',
    nextStatus: 'pending',
    title: 'Initial Setup',
    actionText: 'Mark as Ready',
    component: BaseRequestStep,
  },
  {
    currentStatus: 'pending',
    nextStatus: 'request_submitted',
    title: 'Send to Provider',
    actionText: 'Send Request',
    component: ChannelSubmitStep,
  },
  {
    currentStatus: 'request_submitted',
    nextStatus: 'request_received',
    title: 'Confirm Receipt',
    actionText: 'Confirm Received',
    component: BaseRequestStep,
  },
  {
    currentStatus: 'request_received',
    nextStatus: 'awaiting_invoice',
    title: 'Follow-Up on Processing',
    actionText: 'Mark Invoice Pending',
    component: BaseRequestStep,
  },
  {
    currentStatus: 'awaiting_invoice',
    nextStatus: 'pending_payment',
    title: 'Pay the Invoice',
    actionText: 'Submit Invoice',
    component: InvoiceReadyStep,
  },
  {
    currentStatus: 'pending_payment',
    nextStatus: 'records_pending',
    title: 'Confirm Payment Receipt',
    actionText: 'Mark Payment Complete',
    component: BaseRequestStep,
  },
  {
    currentStatus: 'records_pending',
    nextStatus: 'records_delivered',
    title: 'Wait for Records',
    actionText: 'Mark Records Delivered',
    component: BaseRequestStep,
  },
  {
    currentStatus: 'records_delivered',
    nextStatus: 'records_ingested',
    title: 'Upload to Finch',
    actionText: 'Upload Records',
    component: RecordIngestedStep,
  },
  {
    currentStatus: 'records_ingested',
    nextStatus: 'rejected',
    title: 'Rejected',
    actionText: 'Mark as Rejected',
    component: BaseRequestStep,
  },
  {
    currentStatus: 'rejected',
    nextStatus: 'cancelled',
    title: 'Cancelled',
    actionText: 'Mark as Cancelled',
    component: BaseRequestStep,
  },
];

interface RecordsProgressProps {
  provider: MedicalProvider;
  request: TreatmentRecordRequestSchema;
}

export const RecordsPizzaTracker = ({
  request,
  provider,
}: RecordsProgressProps) => {
  const currentStepIndex = StatusOptions.findIndex(
    (status) => status.value === request.status,
  );

  const getStepState = (state: TreatmentRecordRequestStatus) => {
    const index = StatusOptions.findIndex((status) => status.value === state);
    if (index <= currentStepIndex) {
      return 'complete';
    }

    // Mark as 'up_next' if it's a valid transition from current status
    if (index === currentStepIndex + 1) {
      return 'up_next';
    }

    return 'not_started';
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs">
        <span className="uppercase font-semibold text-black/50">
          Records Progress
        </span>
      </div>
      <div className="flex flex-row gap-2 overflow-x-scroll no-scrollbar">
        {REQUEST_STEPS.map((step) => {
          const stepState = getStepState(step.nextStatus);

          return (
            <step.component
              key={step.nextStatus}
              state={stepState}
              request={request}
              provider={provider}
              status={step.currentStatus}
              nextStatus={step.nextStatus}
              title={step.title}
              actionText={step.actionText}
            />
          );
        })}
      </div>
    </div>
  );
};
